import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle } from "lucide-react";
import DateLabelInput from "../common/forms/DateLabelInput";
import { MdLockReset } from "react-icons/md";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import Pagination from "../common/table/tablepagination";
import { useAuth } from "../../context/AuthContext"; // Auth context

export default function Balancedutypendingtopayindispatchreport() {
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ startDate: "", endDate: "" });
    const { logout } = useAuth();

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    const handleDateChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilters({ startDate: "", endDate: "" });
        setCurrentPage(1);
        fetchReport();
    };

    // Fetch report
    const fetchReport = useCallback(async (start = "", end = "") => {
        setLoading(true);
        let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.GET_BALANCE_DUTY_DISPATCH}`;
        if (start && end) url += `?start_date=${start}&end_date=${end}`;
        try {
            let res = await fetch(url, { method: "GET", headers: getHeaders(true) });

            // -------------------------
            // 🔥 Handle 401 token expired
            // -------------------------
            if (res.status === 401) {
                console.warn("Access token expired. Attempting refresh...");
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry the same API call with new token
                    res = await fetch(url, { method: "GET", headers: getHeaders(true) });
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout(); // redirect user
                    return;
                }
            }
            const result = await res.json();
            if (result.status === 200) setReportData(result.data);
            else setReportData([]);
        } finally {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => { fetchReport(); }, [fetchReport]);
    useEffect(() => {
        if (filters.startDate && filters.endDate) {
            fetchReport(filters.startDate, filters.endDate);
        }
    }, [filters.startDate, filters.endDate, fetchReport]);

    // Pagination calculations
    const indexOfLastRecord = currentPage * rowsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - rowsPerPage;
    const currentRecords = reportData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(reportData.length / rowsPerPage);

    // Columns
    const columns = ["BE No.", "Exbond Weight", "Dispatch Weight", "Balance Weight"];

    const totalBalanceWeight = reportData.reduce(
        (sum, row) => sum + (row.data?.balance_weight || 0),
        0
    );

    const formatIndianNumber = (num) => {
        if (num === null || num === undefined || isNaN(num)) return "0";
        return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };


    return (
        <div className="w-full xl:w-1/2 bg-white p-6 rounded border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-dashed pb-3 mb-5">
                <div className="flex items-center gap-2">
                    <CheckCircle className="text-indigo-500" size={20} />
                    <h2 className="text-base font-semibold text-gray-900">
                        Balance Quantity & Duty (Exbond & Dispatch)
                    </h2>
                </div>
            </div>

            {/* Date Filters */}
            <div className="flex items-end gap-4 mb-6 flex-wrap xl:flex-nowrap">
                <div className="w-full sm:w-auto">
                    <DateLabelInput
                        label="Start Date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={(e) => handleDateChange("startDate", e.target.value)}
                    />
                </div>
                <div className="w-full sm:w-auto">
                    <DateLabelInput
                        label="End Date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={(e) => handleDateChange("endDate", e.target.value)}
                    />
                </div>
                {(filters.startDate || filters.endDate) && (
                    <button
                        onClick={resetFilters}
                        className="flex items-center justify-center h-11 w-11"
                        title="Reset Filters"
                    >
                        <MdLockReset size={20} />
                    </button>
                )}
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}
            {!loading && reportData.length === 0 && <div className="text-gray-500 text-center">No data available</div>}

            {reportData.length > 0 && (
                <div className="overflow-x-auto">

                    {/* Table Header */}
                    <div className="flex text-xs font-semibold text-gray-700 border-b border-gray-200">
                        {columns.map((col, i) => (
                            <div
                                key={i}
                                className="w-[25%] px-4 py-2.5 text-center"
                            >
                                {col}
                            </div>
                        ))}
                    </div>

                    {/* Table Rows */}
                    <div className="divide-y divide-gray-100 text-table text-gray-800">
                        {currentRecords.map((row, i) => (
                            <div key={i} className="flex items-center">
                                <div className="w-[25%] px-4 py-2.5 text-center">{formatIndianNumber(row.inbond_be_number)}</div>
                                <div className="w-[25%] px-4 py-2.5 text-center">{formatIndianNumber(row.data.total_exbond_weight)}</div>
                                <div className="w-[25%] px-4 py-2.5 text-center">{formatIndianNumber(row.data.total_dispatch_weight)}</div>
                                <div className="w-[25%] px-4 py-2.5 text-center font-semibold">
                                    {formatIndianNumber(row.data.balance_weight)}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Table Footer: Total */}
                    <div className="flex font-semibold text-gray-900 border-t border-gray-200">
                        <div className="w-[25%] px-4 py-2.5 text-center">Total</div>
                        <div className="w-[25%] px-4 py-2.5 text-center"></div>
                        <div className="w-[25%] px-4 py-2.5 text-center"></div>
                        <div className="w-[25%] px-4 py-2.5 text-center">{formatIndianNumber(totalBalanceWeight)}</div>
                    </div>

                    {/* Pagination */}
                    {reportData.length > rowsPerPage && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => setCurrentPage(page)}
                            rowsPerPage={rowsPerPage}
                            totalItems={reportData.length}
                        />
                    )}
                </div>
            )}

        </div>
    );
}
